import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import TaskCard from './TaskCard';
import { TrendingUp, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewProjects = ({ user, onBackToDashboard }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalProjects: 0,
    completedProjects: 0,
    inProgressProjects: 0,
    pendingProjects: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/targets?category=project`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      // Sort by priority (lower priority number = higher priority)
      const projectData = response.data.sort((a, b) => (a.priority || 999) - (b.priority || 999));
      setProjects(projectData);
      
      // Calculate metrics
      const total = projectData.length;
      const completed = projectData.filter(p => {
        const tasks = p.tasks || [];
        const completedTasks = tasks.filter(t => t.completed).length;
        return tasks.length > 0 && completedTasks === tasks.length;
      }).length;
      
      const inProgress = projectData.filter(p => {
        const tasks = p.tasks || [];
        const completedTasks = tasks.filter(t => t.completed).length;
        return tasks.length > 0 && completedTasks > 0 && completedTasks < tasks.length;
      }).length;
      
      const pending = total - completed - inProgress;
      
      setMetrics({
        totalProjects: total,
        completedProjects: completed,
        inProgressProjects: inProgress,
        pendingProjects: pending
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (targetId, updates) => {
    try {
      await axios.put(`${API}/targets/${targetId}`, updates, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      // Refetch projects to update the UI
      await fetchProjects();
    } catch (error) {
      console.error('Error updating target:', error);
      throw error; // Re-throw to let TaskCard handle the error
    }
  };

  const metricCards = [
    {
      title: 'Total Projects',
      value: metrics.totalProjects,
      icon: TrendingUp,
      gradient: 'from-blue-500 to-blue-600',
      textColor: 'text-blue-600'
    },
    {
      title: 'Completed',
      value: metrics.completedProjects,
      icon: CheckCircle,
      gradient: 'from-green-500 to-green-600',
      textColor: 'text-green-600'
    },
    {
      title: 'In Progress',
      value: metrics.inProgressProjects,
      icon: Clock,
      gradient: 'from-yellow-500 to-yellow-600',
      textColor: 'text-yellow-600'
    },
    {
      title: 'Pending',
      value: metrics.pendingProjects,
      icon: AlertCircle,
      gradient: 'from-red-500 to-red-600',
      textColor: 'text-red-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 -mx-6 -mt-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-5 shadow-lg rounded-t-xl flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Projects</h2>
          <p className="text-slate-300 mt-1">Track and manage your project progress</p>
        </div>
        {onBackToDashboard && (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white hover:bg-white/30 border border-white/30 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
        )}
      </div>

      <div className="p-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${card.gradient}`}></div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">{card.title}</p>
                      <p className={`text-3xl font-bold ${card.textColor} mt-2`}>
                        {card.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-full bg-gradient-to-br ${card.gradient}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Project Cards */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Project Cards</h2>
          
          {projects.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl shadow-md">
              <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <p className="text-lg text-slate-600">No projects found</p>
              <p className="text-sm text-slate-500 mt-2">Projects will appear here once created</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {projects.map((project) => (
                <TaskCard
                  key={project.id}
                  target={project}
                  onUpdate={handleUpdate}
                  user={user}
                />
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900">About Project Cards</h3>
              <p className="text-blue-800 text-sm mt-1">
                Each project card can contain up to 10 sub-tasks. Track progress, assign team members, set deadlines, and add notes to stay organized.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewProjects;
